import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

function DiseaseDetail() {
  const { id } = useParams();
  const [disease, setDisease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [tghNews, setTghNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  const renderMissing = (sectionName) => (
    <p className="text-muted">
      Detailed {sectionName.toLowerCase()} information is not available in this ICD-11 entry yet.
    </p>
  );

  // Format text with markdown-style formatting
  const formatText = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((paragraph, index) => {
      if (!paragraph.trim()) return <br key={index} />;
      
      // Check for headings
      if (paragraph.trim().startsWith('#')) {
        const match = paragraph.match(/^(#{1,3})\s+(.+)$/);
        if (match) {
          const level = match[1].length;
          const content = match[2];
          const HeadingTag = `h${level + 2}`;
          return (
            <HeadingTag key={index} className="mb-3" style={{ fontWeight: 'bold', fontSize: level === 1 ? '1.5rem' : level === 2 ? '1.3rem' : '1.1rem' }}>
              {formatInlineText(content)}
            </HeadingTag>
          );
        }
      }
      
      return (
        <p key={index} className="mb-3">
          {formatInlineText(paragraph)}
        </p>
      );
    });
  };

  // Format inline text (bold, etc.)
  const formatInlineText = (text) => {
    const parts = [];
    let lastIndex = 0;
    const regex = /\*\*(.+?)\*\*/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(<strong key={match.index}>{match[1]}</strong>);
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    // Guard against invalid dates
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    fetchDisease();
    fetchNews();
    setActiveTab('overview');
  }, [id]);

  const fetchDisease = async () => {
    try {
      const isIcd11Id = id.startsWith('icd11-');
      let decodedUri = null;
      if (isIcd11Id) {
        try {
          decodedUri = decodeURIComponent(id.replace('icd11-', ''));
        } catch (_e) {
          decodedUri = id.replace('icd11-', '');
        }
      }
      const response = isIcd11Id
        ? await axios.get('/api/diseases/icd/entity', {
            params: { uri: decodedUri }
          })
        : await axios.get(`/api/diseases/${id}?enrich_icd11=false`);
      setDisease(response.data);
    } catch (error) {
      console.error('Error fetching disease:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      setNewsLoading(true);
      const response = await axios.get('/api/news');
      const news = Array.isArray(response.data) ? response.data : response.data?.news || [];

      // Sort newest first (like the News page expects)
      news.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTghNews(news);
    } catch (error) {
      console.error('Error fetching news:', error);
      setTghNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!disease) {
    return (
      <div className="container py-5">
        <h1>Disease Not Found</h1>
        <Link to="/diseases">Back to Diseases & Conditions</Link>
      </div>
    );
  }

  const isIcdDetail = disease?.source === 'icd11';

  // Build tabs array - always show full clinical structure for ICD detail pages
  const tabs = disease ? (() => {
    if (isIcdDetail) {
      return [
        { id: 'overview', label: 'Overview', content: disease.overview || disease.description },
        { id: 'symptoms', label: 'Symptoms & causes', content: disease.symptoms || disease.causes },
        { id: 'risk-factors', label: 'Risk factors', content: disease.risk_factors },
        { id: 'complications', label: 'Complications', content: disease.complications },
        { id: 'prevention', label: 'Prevention', content: disease.prevention },
        { id: 'diagnosis', label: 'Diagnosis & treatment', content: disease.diagnosis_treatment }
      ];
    }

    const tabList = [];
    if (disease.overview || disease.description) {
      tabList.push({ id: 'overview', label: 'Overview', content: disease.overview || disease.description });
    }
    if (disease.symptoms || disease.causes) {
      tabList.push({ id: 'symptoms', label: 'Symptoms & causes', content: disease.symptoms || disease.causes });
    }
    if (disease.risk_factors) {
      tabList.push({ id: 'risk-factors', label: 'Risk factors', content: disease.risk_factors });
    }
    if (disease.complications) {
      tabList.push({ id: 'complications', label: 'Complications', content: disease.complications });
    }
    if (disease.prevention) {
      tabList.push({ id: 'prevention', label: 'Prevention', content: disease.prevention });
    }
    if (disease.diagnosis_treatment) {
      tabList.push({ id: 'diagnosis', label: 'Diagnosis & treatment', content: disease.diagnosis_treatment });
    }
    return tabList;
  })() : [];

  return (
    <>
      {/* Hero Section - Dark Blue Background */}
      <section className="py-5 tgh-page-hero-band" style={{ backgroundColor: 'var(--tgh-page-header)', color: 'var(--tgh-on-page-header)' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: 'var(--tgh-on-page-header)' }}>Home</Link></li>
              <li className="breadcrumb-item"><Link to="/diseases" style={{ color: 'var(--tgh-on-page-header)' }}>Diseases & Conditions</Link></li>
              <li className="breadcrumb-item active" style={{ color: 'var(--tgh-on-page-header)' }}>{disease.name}</li>
            </ol>
          </nav>

          <h1 className="display-4 mb-4" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            {disease.name}
          </h1>

          {/* Alias display removed to keep disease pages focused */}

          {/* Add ICD-11 Code Display */}
          {disease.icd11_code && (
            <p className="mb-4" style={{ fontSize: '1rem', opacity: 0.9 }}>
              <strong>ICD-11 Code:</strong> {disease.icd11_code}
              {disease.icd11_uri && disease.source !== 'icd11' && (
                <a 
                  href={disease.icd11_uri} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ms-2"
                  style={{ color: 'var(--tgh-on-page-header)', textDecoration: 'underline' }}
                >
                  View in ICD-11 Browser
                </a>
              )}
            </p>
          )}

          <Link to="/contact" className="btn btn-light btn-lg mt-3" style={{ textDecoration: 'none' }}>
            <i className="bi bi-calendar-check me-2"></i>
            Contact us
          </Link>
        </div>
      </section>

      {/* Tab Navigation */}
      <section style={{ backgroundColor: 'var(--tgh-blue)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="container">
          <div className="d-flex flex-wrap" style={{ gap: '0' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="btn"
                style={{
                  backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                  color: activeTab === tab.id ? 'var(--tgh-blue)' : '#fff',
                  border: 'none',
                  borderRadius: 0,
                  padding: '15px 20px',
                  fontWeight: activeTab === tab.id ? '600' : '400',
                  fontSize: '0.95rem'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-5" style={{ backgroundColor: '#fff', minHeight: '60vh' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-9">
              <p className="tgh-diseases-disclaimer mb-4" role="note">
                This is for informational purposes only. For medical advice or a diagnosis, consult a professional or
                healthcare provider.
              </p>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Overview</h2>
                  <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                    {disease.overview || disease.description
                      ? formatText(disease.overview || disease.description)
                      : renderMissing('Overview')}
                  </div>
                </div>
              )}

              {/* Symptoms & Causes Tab */}
              {activeTab === 'symptoms' && (
                <div>
                  {disease.symptoms ? (
                    <>
                      <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Symptoms</h2>
                      <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                        {formatText(disease.symptoms)}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Symptoms</h2>
                      {renderMissing('Symptoms')}
                    </>
                  )}
                  
                  {disease.causes ? (
                    <div className={disease.symptoms ? 'mt-5' : ''}>
                      <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Causes</h2>
                      <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                        {formatText(disease.causes)}
                      </div>
                    </div>
                  ) : (
                    <div className={disease.symptoms ? 'mt-5' : ''}>
                      <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Causes</h2>
                      {renderMissing('Causes')}
                    </div>
                  )}
                </div>
              )}

              {/* Risk Factors Tab */}
              {activeTab === 'risk-factors' && (
                <div>
                  <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Risk factors</h2>
                  <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                    {disease.risk_factors ? formatText(disease.risk_factors) : renderMissing('Risk factors')}
                  </div>
                </div>
              )}

              {/* Complications Tab */}
              {activeTab === 'complications' && (
                <div>
                  <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Complications</h2>
                  <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                    {disease.complications ? formatText(disease.complications) : renderMissing('Complications')}
                  </div>
                </div>
              )}

              {/* Prevention Tab */}
              {activeTab === 'prevention' && (
                <div>
                  <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Prevention</h2>
                  <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                    {disease.prevention ? formatText(disease.prevention) : renderMissing('Prevention')}
                  </div>
                </div>
              )}

              {/* Diagnosis & Treatment Tab */}
              {activeTab === 'diagnosis' && (
                <div>
                  <h2 className="mb-4" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Diagnosis & treatment</h2>
                  <div style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
                    {disease.diagnosis_treatment ? formatText(disease.diagnosis_treatment) : renderMissing('Diagnosis and treatment')}
                  </div>
                </div>
              )}

              {/* Contact CTA Button */}
              <div className="mt-5">
                <Link to="/contact" className="btn btn-primary btn-lg" style={{ textDecoration: 'none' }}>
                  Contact us
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-3">
              <div className="border-start ps-4" style={{ borderColor: '#e0e0e0' }}>
                <h4 className="mb-3" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>TGH Press</h4>
                <p className="text-muted small mb-3">Latest updates from Tema General Hospital Media Center.</p>

                {newsLoading ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <div className="small text-muted mt-2">Loading news...</div>
                  </div>
                ) : tghNews.length === 0 ? (
                  <p className="text-muted small mb-0">No news articles available at this time.</p>
                ) : (
                  <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {tghNews.map((post) => (
                      <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <Link
                          to={`/news/${post.id}`}
                          style={{ color: 'var(--tgh-blue-light)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {post.title}
                        </Link>
                        {post.date && (
                          <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                            {formatDate(post.date)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default DiseaseDetail;
